// Browser side of file storage. Mirrors the supabase.storage.from(bucket) calls the app already makes:
// /api/storage checks the caller and returns a short-lived presigned link; the file itself goes
// straight between the browser and Neon Object Storage.
const API = '/api/storage'

async function requestLink(getSession, body) {
  const { data } = await getSession()
  const token = data?.session?.access_token
  if (!token) return { data: null, error: new Error('Not signed in') }

  const res = await fetch(API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) return { data: null, error: new Error(json.error || `Storage request failed (${res.status})`) }
  return { data: json, error: null }
}

// Presigned links expire after at most 15 minutes. When an <img> pointing at Neon storage fails to load
// (typically a viewer left open), fetch a fresh link once and retry. Works for every image in the app
// without touching call sites: object URLs are path-style, /<bucket>/<key>.
const RETRY_WINDOW_MS = 60 * 1000

export function installExpiredLinkRetry(getSession) {
  if (typeof document === 'undefined') return

  document.addEventListener('error', async (event) => {
    const img = event.target
    if (!(img instanceof HTMLImageElement)) return

    let url
    try { url = new URL(img.currentSrc || img.src) } catch { return }
    if (!url.hostname.endsWith('.neon.tech') || !url.hostname.includes('.storage.')) return

    // One retry per failure: don't loop if the fresh link also fails.
    const last = Number(img.dataset.linkRetryAt || 0)
    if (Date.now() - last < RETRY_WINDOW_MS) return
    img.dataset.linkRetryAt = String(Date.now())

    const [bucket, ...rest] = url.pathname.replace(/^\//, '').split('/')
    const path = rest.map(decodeURIComponent).join('/')
    if (!bucket || !path) return

    const { data } = await requestLink(getSession, { action: 'download', bucket, path })
    if (data?.url) img.src = data.url
  }, true)
}

export function createStorage(getSession) {
  return {
    from(bucket) {
      return {
        async upload(path, file) {
          const contentType = file.type || 'application/octet-stream'
          const { data, error } = await requestLink(getSession, { action: 'upload', bucket, path, contentType })
          if (error) return { data: null, error }
          const put = await fetch(data.url, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file })
          if (!put.ok) return { data: null, error: new Error(`Upload failed (${put.status})`) }
          return { data: { path }, error: null }
        },

        async createSignedUrl(path, expiresIn) {
          const { data, error } = await requestLink(getSession, { action: 'download', bucket, path, expiresIn })
          if (error) return { data: null, error }
          return { data: { signedUrl: data.url }, error: null }
        },

        async remove(paths) {
          const { error } = await requestLink(getSession, { action: 'delete', bucket, paths })
          if (error) return { data: null, error }
          return { data: paths.map(name => ({ name })), error: null }
        },
      }
    },
  }
}
